import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSocket } from "@/lib/useSocket";
import { CheckCircle2, XCircle, Clock, Share2, Ban } from "lucide-react";
import { toast } from "sonner";
import { fmtSydneyRange } from "@/lib/timezone";

// Consumer landing where a friend arriving via the share link can browse
// other openings. Falls back to the current origin (which also handles
// ?biz=<id> as a deep-link) so the button works even before a consumer
// app is deployed.
const CONSUMER_URL = (process.env.REACT_APP_CONSUMER_URL || "").replace(/\/+$/, "");
function bizShareUrl(businessId) {
  if (!businessId) return "";
  if (CONSUMER_URL) return `${CONSUMER_URL}/business/${businessId}`;
  if (typeof window === "undefined") return "";
  return `${window.location.origin}/?biz=${businessId}`;
}

const STATUS_META = {
  pending: {
    label: "Awaiting response",
    color: "#C9A85F",
    Icon: Clock,
    copy: "The business has been notified. Watch this page — status updates in real time.",
  },
  approved: {
    label: "Approved",
    color: "#34D399",
    Icon: CheckCircle2,
    copy: "You're booked! The business will see you at the appointed time.",
  },
  declined: {
    label: "Declined",
    color: "#F87171",
    Icon: XCircle,
    copy: "Unfortunately this slot was declined or filled by someone else.",
  },
  cancelled: {
    label: "Cancelled",
    color: "#9CA3AF",
    Icon: Ban,
    copy: "You cancelled this booking. The slot is back on the map for other customers.",
  },
};

export default function RequestStatus() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/requests/status/${id}`);
      setData(data);
    } catch {
      setData(false);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Live status updates via WebSocket (no polling)
  useSocket(`status/${id}`, () => load(), !!id);

  // Consumer-initiated cancel. Confirms first so a mis-tap can't wipe a
  // hard-won booking. The endpoint uses the request id as a capability
  // token, matching the sibling GET /requests/status/{id} pattern — no
  // auth header required, works for guest bookings.
  const cancelBooking = useCallback(async () => {
    const req = data?.request;
    if (!req || cancelling) return;
    const wasApproved = req.status === "approved";
    const confirmCopy = wasApproved
      ? "Cancel this confirmed booking? The salon will be notified and the slot will re-open for other customers."
      : "Withdraw this booking request? The salon will be notified.";
    if (!window.confirm(confirmCopy)) return;
    setCancelling(true);
    try {
      await api.post(`/requests/status/${id}/cancel`);
      toast.success(wasApproved ? "Booking cancelled" : "Request withdrawn");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't cancel — try again");
    } finally {
      setCancelling(false);
    }
  }, [data, cancelling, id, load]);

  // Share the business behind this booking. On mobile / installed PWAs this
  // opens the native share sheet; on desktop we fall back to clipboard copy.
  // Only surfaced when the request is approved — that's the highest-intent
  // virality moment (consumer just locked in a slot and is buzzing about it).
  const shareBusiness = useCallback(async () => {
    const opening = data?.opening;
    if (!opening) return;
    const url = bizShareUrl(opening.business_id);
    const text = `Just grabbed a same-day slot at ${opening.business_name} on Book Away 🔥`;
    const payload = { title: "Book Away", text, url };
    try {
      if (navigator.share) {
        await navigator.share(payload);
        return;
      }
    } catch (err) {
      // User cancelled the share sheet — don't fall through to clipboard.
      if (err?.name === "AbortError") return;
    }
    try {
      await navigator.clipboard.writeText(`${text} ${url}`);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Couldn't share — copy the URL manually");
    }
  }, [data]);

  if (loading) {
    return (
      <div className="mx-auto max-w-xl p-8 mono text-xs text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="mx-auto max-w-xl p-8">
        <p className="mono text-xs text-muted-foreground">Request not found.</p>
        <Link
          to="/"
          data-testid="status-back-link"
          className="mt-4 inline-block underline underline-offset-4"
        >
          ← Back to map
        </Link>
      </div>
    );
  }

  const req = data.request;
  const opening = data.opening;
  const meta = STATUS_META[req.status] || STATUS_META.pending;
  const Icon = meta.Icon;

  return (
    <div className="mx-auto max-w-xl p-4 md:p-8">
      <div className="border border-border">
        <div className="border-b border-border p-6 md:p-8">
          <p className="mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
            → your request
          </p>
          <div
            className="mt-4 inline-flex items-center gap-2 border border-foreground px-3 py-1.5"
            data-testid={`status-badge-${req.status}`}
            style={{ color: meta.color, borderColor: meta.color }}
          >
            <Icon size={14} />
            <span className="mono text-[11px] uppercase tracking-[0.25em]">
              {meta.label}
            </span>
          </div>
          <h1 className="mt-4 text-3xl leading-tight tracking-tight md:text-4xl">
            {opening?.title || "Booking request"}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">{meta.copy}</p>
        </div>

        {opening && (
          <div className="grid grid-cols-2 border-b border-border">
            <div className="border-r border-border p-4 md:p-6">
              <p className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Business
              </p>
              <p className="mt-1 text-sm">{opening.business_name}</p>
            </div>
            <div className="p-4 md:p-6">
              <p className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Service
              </p>
              <p className="mt-1 text-sm">{opening.service_type}</p>
            </div>
            <div className="border-r border-t border-border p-4 md:p-6">
              <p className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Address
              </p>
              <p className="mt-1 text-sm">{opening.address}</p>
            </div>
            <div className="border-t border-border p-4 md:p-6">
              <p className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Time
              </p>
              <p className="mt-1 text-sm">
                {fmtSydneyRange(opening.start_time, opening.end_time)}
              </p>
            </div>
          </div>
        )}

        <div className="p-6 md:p-8">
          <p className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Reference
          </p>
          <p className="mono mt-1 text-xs break-all" data-testid="status-request-id">
            {req.id}
          </p>
          <p className="mt-4 text-xs text-muted-foreground">
            Bookmark this page or share the URL — you can come back anytime to
            check the status.
          </p>
        </div>

        {(req.status === "pending" || req.status === "approved") && (
          <div className="border-t border-border p-6 md:p-8">
            <p className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
              → change of plans?
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              {req.status === "approved"
                ? "Something come up? Cancel your booking and the salon will know — the slot will go back on the map."
                : "Don't need it anymore? Withdraw your request and the salon won't wait for you."}
            </p>
            <button
              type="button"
              onClick={cancelBooking}
              disabled={cancelling}
              data-testid="status-cancel-btn"
              className="mono mt-4 inline-flex items-center gap-2 border border-[#F87171] px-4 py-2.5 text-[11px] uppercase tracking-[0.25em] text-[#B91C1C] transition-colors hover:bg-[#F87171]/10 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Ban size={14} />
              {cancelling
                ? "Cancelling…"
                : req.status === "approved"
                ? "Cancel booking"
                : "Withdraw request"}
            </button>
          </div>
        )}

        {req.status === "approved" && opening?.business_id && (
          <div className="border-t border-border p-6 md:p-8">
            <p className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
              → love it? share the find
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              Tell a friend where you booked — they can grab another open slot
              from {opening.business_name} on Book Away.
            </p>
            <button
              type="button"
              onClick={shareBusiness}
              data-testid="status-share-business-btn"
              className="mono mt-4 inline-flex items-center gap-2 border border-foreground bg-foreground px-4 py-2.5 text-[11px] uppercase tracking-[0.25em] text-background transition-colors hover:bg-transparent hover:text-foreground"
            >
              <Share2 size={14} />
              Share {opening.business_name}
            </button>
          </div>
        )}
      </div>

      <div className="mt-4">
        <Link
          to="/"
          data-testid="status-back-link"
          className="mono text-[11px] uppercase tracking-widest underline underline-offset-4 hover:text-[#C9A85F]"
        >
          ← Back to map
        </Link>
      </div>
    </div>
  );
}
